<script setup lang="ts">
// The centerpiece: six questions → deterministic verdict. The client scorer
// gives the instant display; when the visitor asks for quotes, the raw
// answers travel with the contact and the backend recomputes the authority
// version for the relayed summary.
import { ref } from 'vue'
import AiAnalysisPanel from '@/shared/components/AiAnalysisPanel.vue'
import TreeWalker from '@/shared/components/TreeWalker.vue'
import type { Answer, DecisionTree } from '@/shared/diagnostic/types'
import wizardJson from '@contracts/decision-tree.shouldi.v1.json'
import VerdictCard from '../components/VerdictCard.vue'
import { scoreWizard, type WizardVerdict } from '../scorer'
import { contactContext, scrollTo } from '../store'

const tree = wizardJson as unknown as DecisionTree

const walker = ref<InstanceType<typeof TreeWalker> | null>(null)
const verdict = ref<WizardVerdict | null>(null)
const finishedAnswers = ref<Answer[]>([])

function onComplete(answers: Answer[]) {
    verdict.value = scoreWizard(tree, answers)
    finishedAnswers.value = answers
    contactContext.value = { tree_id: tree.id, answers }
}

function onGetQuotes() {
    scrollTo('contact')
}

function startOver() {
    verdict.value = null
    contactContext.value = undefined
    walker.value?.reset()
}
</script>

<template>
    <section id="wizard" class="site-section band">
        <div class="site-content narrow">
            <span class="kicker" v-reveal>Step by step</span>
            <h2 v-reveal>The decision wizard</h2>
            <p class="muted intro">
                Answer honestly — there's no wrong answer and nothing is sold on the other side.
            </p>

            <div v-if="!verdict" v-reveal class="card">
                <TreeWalker
                    ref="walker"
                    :tree="tree"
                    why-variant="aside"
                    data-use-note="Answers stay on this device. They're only sent anywhere if you ask for quotes at the end — attached so the pro quotes your actual situation."
                    @complete="onComplete"
                />
            </div>
            <template v-else>
                <VerdictCard :verdict="verdict" @get-quotes="onGetQuotes" @start-over="startOver" />
                <AiAnalysisPanel
                    :tree="tree"
                    :answers="finishedAnswers"
                    pitch="The score above is pure math. The free AI second opinion reads your actual answers — age, repair quote, symptoms — and explains the call in plain English. No signup."
                />
            </template>
        </div>
    </section>
</template>

<style scoped lang="scss">
.band {
    background-color: var(--site-bg-alt);
    background-image: var(--site-pattern, none);
}

.narrow {
    max-width: 56rem;
}

.intro {
    margin-bottom: var(--site-space-lg);
}

.card {
    background: var(--site-surface);
    border: 2px solid var(--site-text);
    border-radius: var(--site-radius-md);
    box-shadow: var(--site-shadow-md);
    padding: var(--site-space-lg);
}
</style>
